
import 'mapbox-gl/dist/mapbox-gl.css';
import _ from 'lodash';
import { defineComponent, ref, onMounted, onBeforeMount, computed, watch, PropType } from 'vue';
import mapboxgl, {
    Layer as MapboxLayer,
    GeoJSONSourceRaw,
    GeoJSONSource,
    NavigationControl,
    LngLatBounds
} from 'mapbox-gl';
import ResizeObserver from 'resize-observer-polyfill';

import MapboxButtonControl from './MapboxButtonControl';

export type Coordinates = [number, number];

export type Source = {
    id: string;
    // TODO: Support other source types
    data: GeoJSONSourceRaw;
}

export type LayerOptions = {
    clickable?: boolean;
    hoverable?: boolean;
    beforeId?: string;
}

export type Layer = MapboxLayer & LayerOptions;

export type ZoomToFeaturesOptions = {
    wrapUsa?: boolean;
    animate?: boolean;
    padding?: number;
}

export type ZoomToUsaBoundsOptions = {
    animate?: boolean;
    padding?: number;
}

export const CENTER_OF_CONTINENTAL_USA: Coordinates = [-95.86789, 37.04455]; // [-96.4247, 39.51073];
export const CENTER_OF_ALBERS_USA: Coordinates = [0, 0];
export const CENTER_OF_ALBERTA: Coordinates = [-114.99920882732305, 54.90243566064806];

export const USA_BOUNDS:[Coordinates, Coordinates] = [[-124.848974, 24.396308], [-66.885444, 49.384358]];
export const ALBERS_USA_BOUNDS:[Coordinates, Coordinates] = [[-26.0696, -13.5095], [26.0696, 13.5095]];
export const ALBERTA_BOUNDS:[Coordinates, Coordinates] = [
    [-120.000133, 48.999625],
    [-110.005023, 59.999848],
];

export const DEFAULT_ZOOM = 3;
export const DEFAULT_ZOOM_MIN = 3;
export const DEFAULT_ZOOM_MAX = 10;

const TWO_FINGER_WARNING = 'Use two fingers to move the map';
const CTRL_SCROLL_WARNING = 'Use ctrl + scroll to zoom the map';

export default defineComponent({
    name: 'MapboxGLMap',
    props: {
        layers: {
            type: Array as () => Layer[],
            default: () => []
        },
        sources: {
            type: Array as () => Source[],
            default: () => []
        },
        accessToken: {
            type: String,
            required: true
        },
        mapStyle: {
            type: String,
            default: () => 'mapbox://styles/mapbox/light-v9'
        },
        mapOptions: {
            type: Object,
            default: () => ({})
        },
        centerCoordinates: {
            type: Object as PropType<Coordinates>,
            default: () => CENTER_OF_CONTINENTAL_USA,
        },
        customControls: {
            type: Array as PropType<MapboxButtonControl[]>,
            default: () => [],
        },
        isAlbersUsa: {
            type: Boolean,
            default: false,
        },
        mapBounds: {
            type: LngLatBounds,
            default: new LngLatBounds(),
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
        useCtrlZoom: {
            type: Boolean,
            default: false
        },
        withNavigation: {
            type: Boolean,
            default: false
        },
    },
    emits: ['map-loaded', 'loaded', 'hover', 'click', 'on-moveend', 'zoomend'],
    setup(props, { emit }) {
        const mapRef = ref<HTMLElement>();
        const mapWarningRef = ref<HTMLElement>();
        const mapRootRef = ref<Element>();
        const tooltipRef = ref<HTMLElement>();

        const map = ref<mapboxgl.Map>();
        const resizeObserver = ref<ResizeObserver>();
        const isLoaded = ref<boolean>(false);
        const mapWarning = ref(TWO_FINGER_WARNING);
        const isScrolling = ref<number | undefined>();

        // computed
        const layersAndSources = computed((): {isLoaded: boolean; layers: Layer[]; sources: Source[]} => {
            const sourceIds = _(props.sources)
                .keyBy('id')
                .mapValues(() => true)
                .value();
            return {
                isLoaded: isLoaded.value,
                sources: props.sources.map(source => source),
                layers: props.layers.filter(layer => (typeof layer.source === 'string'
                    ? sourceIds[layer.source]
                    : true))
            };
        });

        // function
        const addCursorEventListeners = (layerName: string) => {
            map.value?.on('mouseenter', layerName, () => {
                if (!map.value) { return; }
                map.value.getCanvas().style.cursor = 'pointer';
            });

            map.value?.on('mouseleave', layerName, (event: any) => {
                if (!map.value) { return; }
                map.value.getCanvas().style.cursor = '';
                emit('hover', { event });
            });
        };

        const removeCursorEventListeners = (layerName: string) => {
            map.value?.off('mouseenter', layerName, () => {
                if (!map.value) { return; }
                map.value.getCanvas().style.cursor = 'pointer';
            });

            map.value?.off('mouseleave', layerName, (event: any) => {
                if (!map.value) { return; }
                map.value.getCanvas().style.cursor = '';
                emit('hover', { event });
            });
        };

        const removeMapWarning = () => {
            mapWarningRef.value?.classList.add('hidden');
            mapWarningRef.value?.classList.remove('visible');
        };

        const displayMapWarning = (warningMessage: string) => {
            mapWarningRef.value?.classList.remove('hidden');
            mapWarningRef.value?.classList.add('visible');
        };

        const fadeOutMapWarning = () => {
            mapWarningRef.value?.classList.remove('visible');
            window.setTimeout(() => {
                mapWarningRef.value?.classList.add('hidden');
            }, 250);
        };

        const onTouchStart = (event: any) => {
            if (!map.value) return;

            const oe = event.originalEvent;
            if (oe && 'touches' in oe && oe.touches.length > 1) {
                oe.stopImmediatePropagation();
                map.value.dragPan.enable();
                removeMapWarning();
            }
        };

        const onTouchMove = (event: any) => {
            if (!map.value) return;

            const oe = event.originalEvent;
            if (oe && 'touches' in oe && oe.touches.length < 2) {
                displayMapWarning(TWO_FINGER_WARNING);
            }
        };

        const onTouchEnd = () => {
            if (!map.value) return;

            fadeOutMapWarning();
            map.value.dragPan.disable();
        };

        const onMapWarningScroll = (event: WheelEvent) => {
            window.clearTimeout(isScrolling.value);
            if (event.ctrlKey) {
                event.preventDefault();
                removeMapWarning();
            } else {
                isScrolling.value = window.setTimeout(() => {
                    fadeOutMapWarning();
                }, 2000);
            }
        };

        const enableMobileControls = () => {
            if (!map.value) return;

            map.value.dragPan.disable();
            map.value.scrollZoom.disable();
            map.value.touchPitch.disable();

            map.value.on('touchstart', onTouchStart);
            map.value.on('touchmove', onTouchMove);
            map.value.on('touchend', onTouchEnd);
        };

        const disableMobileControls = () => {
            if (!map.value) return;

            map.value.dragPan.enable();
            map.value.scrollZoom.enable();
            map.value.touchPitch.enable();

            map.value.off('touchstart', onTouchStart);
            map.value.off('touchmove', onTouchMove);
            map.value.off('touchend', onTouchEnd);
        };

        // watchers
        watch(() => props.isAlbersUsa, () => {
            if (!map.value) { return; }
            map.value.setStyle(map.value.getStyle(), { diff: false });
        });

        watch(() => props.mapBounds, () => {
            if (!map.value || props.mapBounds.isEmpty()) { return; }
            map.value.fitBounds(props.mapBounds, { animate: true, padding: 50 });
        });

        watch(() => layersAndSources.value, (
            current: {isLoaded: boolean; layers: Layer[]; sources: Source[]},
            prev: {layers: Layer[]; sources: Source[]}
        ) => {
            if (!current.isLoaded) {
                return;
            }

            const prevSourcesById = _.keyBy(prev.sources, s => s.id);
            // const prevLayersById = _.keyBy(prev.layers, l => l.id);

            const removeSources = _.differenceBy(prev.sources, current.sources, s => s.id);
            const updateSources = _(current.sources)
                .intersectionBy(prev.sources, s => s.id)
                .filter(source => source !== prevSourcesById[source.id])
                .value();

            const removeLayers = _.differenceBy(prev.layers, current.layers, l => l.id);
            // const updateLayers = _(current.layers)
            //     .intersectionBy(prev.layers, l => l.id)
            //     .filter(layer => layer !== prevLayersById[layer.id])
            //     .value();

            // Remove layers before sources because a source can't be removed
            // if a layer is using it
            removeLayers.forEach(layer => {
                if (map.value?.getLayer(layer.id)) {
                    if (layer.clickable) {
                        removeCursorEventListeners(layer.id);
                    }
                    map.value?.removeLayer(layer.id);
                }
            });
            removeSources.forEach(source => {
                if (map.value?.getSource(source.id)) {
                    map.value?.removeSource(source.id);
                }
            });

            // Add sources before layers because adding a layer whose source
            // doesn't exist throws an error
            current.sources.forEach(source => {
                if (!map.value?.getSource(source.id)) {
                    map.value?.addSource(source.id, source.data);
                }
                // eslint-disable-next-line
                // @ts-ignore
                (map.value.getSource(source.id) as GeoJSONSource).setData(source.data.data);
            });
            current.layers.forEach(layer => {
                if (!map.value?.getLayer(layer.id)) {
                    map.value?.addLayer(layer as mapboxgl.AnyLayer, layer.beforeId);
                    if (layer.clickable) {
                        addCursorEventListeners(layer.id);
                    }
                }
            });

            updateSources.forEach(source => {
                const existingSource = map.value?.getSource(source.id);
                if (!existingSource) {
                    // This shouldn't happen - means something failed when we
                    // tried to add this source last time
                    console.warn(`Couldn't update source ${source.id} - not found in map`);
                    return;
                }
                // eslint-disable-next-line
                // @ts-ignore
                existingSource.setData(source.data.data);
            });

            // // TODO: Figure out layer updates
            // updateLayers.forEach(layer => {
            //     console.log('vue-mapbox-gl :: update layer (TODO)', layer.id);
            //     // const existingLayer = map.value.getLayer(layer.id);

            //     // existingLayer.paint = layer.paint;
            // });
        });

        watch(() => props.isMobile, (mobile) => {
            if (mobile) {
                enableMobileControls();
            } else {
                disableMobileControls();
            }
        });

        // Lifecycle hools
        onBeforeMount(() => {
            if (resizeObserver.value) {
                resizeObserver.value.disconnect();
            }
            map.value?.remove();
        });

        onMounted(() => {
            if (!props.accessToken) {
                throw new Error('MapboxGlMap component must have accessToken defined to mount');
            }

            if (!mapRef.value) {
                throw new Error('MapboxGlMap component must have an element to mount to');
            }

            mapboxgl.accessToken = props.accessToken;
            map.value = new mapboxgl.Map({
                container: mapRef.value as HTMLElement,
                style: props.mapStyle ?? undefined,
                center: props.centerCoordinates,
                preserveDrawingBuffer: true, // This is needed to download image
                zoom: DEFAULT_ZOOM,
                minZoom: DEFAULT_ZOOM_MIN,
                maxZoom: DEFAULT_ZOOM_MAX,
                ...props.mapOptions
            });

            if (props.customControls) {
                props.customControls.forEach((customControl) => {
                    map.value?.addControl(
                        customControl,
                        customControl.position
                    );
                });
            }

            if (props.withNavigation) {
                map.value.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
            }

            map.value?.on('load', () => {
                emit('map-loaded', map.value);

                map.value?.on('moveend', (e) => {
                    if (e.originalEvent || e.isTrusted) {
                        const bounds = map.value?.getBounds();
                        emit('on-moveend', bounds);
                    }
                });

                // Disable map rotation using right click + drag
                map.value?.dragRotate.disable();

                // Disable map rotation using touch rotation gesture
                map.value?.touchZoomRotate.disableRotation();

                if (props.isMobile) {
                    enableMobileControls();
                }

                map.value?.resize();

                const { sources, layers } = layersAndSources.value;

                sources.forEach(source => {
                    if (!map.value?.getSource(source.id)) {
                        map.value?.addSource(source.id, source.data);
                    }
                });

                layers.forEach(layer => {
                    if (!map.value?.getLayer(layer.id)) {
                        map.value?.addLayer(layer as mapboxgl.AnyLayer, layer.beforeId);
                        if (layer.clickable) {
                            addCursorEventListeners(layer.id);
                        }
                    }
                });

                map.value?.on('click', (event: any) => {
                    const clickableLayers = props.layers
                        .filter(layer => layer.clickable)
                        .filter(layer => map.value?.getLayer(layer.id))
                        .map(layer => layer.id);

                    const features = map.value?.queryRenderedFeatures(
                        event.point,
                        { layers: clickableLayers }
                    );

                    emit('click', features);
                });

                map.value?.on('zoomend', () => {
                    emit('zoomend', map.value?.getZoom());
                });

                map.value?.on('mousemove', _.throttle(event => {
                    if (!map.value) { return; }
                    const { height, width } = map.value?.getContainer().getBoundingClientRect();
                    const point = event.point;
                    const quadrant = `${
                        point.y > (height / 2) ? 'bottom' : 'top'
                    }${
                        point.x > (width / 2) ? 'right' : 'left'
                    }`;

                    const hoverableLayers = props.layers
                        .filter(layer => layer.hoverable)
                        .filter(layer => map.value?.getLayer(layer.id))
                        .map(layer => layer.id);

                    const features = map.value.queryRenderedFeatures(
                        event.point,
                        { layers: hoverableLayers }
                    );

                    if (features.length > 0 && tooltipRef.value) {
                        tooltipRef.value.style.left = `${event.point.x}px`;
                        tooltipRef.value.style.top = `${event.point.y}px`;
                        emit('hover', {
                            event,
                            quadrant,
                            features
                        });
                    } else {
                        emit('hover', { event, quadrant });
                    }
                }, 1000 / 60, { trailing: false }));

                if (props.useCtrlZoom) {
                    map.value?.scrollZoom.disable();

                    map.value?.on('wheel', (event: any) => {
                        window.clearTimeout(isScrolling.value);
                        if (event.originalEvent.ctrlKey) {
                            event.originalEvent.preventDefault();
                            if (!map.value?.scrollZoom.isEnabled()) {
                                map.value?.scrollZoom.enable();
                            }
                            removeMapWarning();
                        } else {
                            if (map.value?.scrollZoom.isEnabled()) {
                                map.value?.scrollZoom.disable();
                            }
                            displayMapWarning(CTRL_SCROLL_WARNING);
                            isScrolling.value = window.setTimeout(() => {
                                fadeOutMapWarning();
                            }, 2000);
                        }
                    });
                }

                isLoaded.value = true;
                emit('map-loaded', map.value);
            });

            mapRef.value?.addEventListener('mouseleave', (event) => {
                emit('hover', { event });
            });

            map.value?.once('idle', () => {
                emit('loaded');
            });

            resizeObserver.value = new ResizeObserver(() => {
                map.value?.resize();
            });

            if (mapRootRef.value) {
                resizeObserver.value.observe(mapRootRef.value);
            }
        });

        return {
            mapRef,
            mapWarningRef,
            mapRootRef,
            tooltipRef,
            mapWarning,
            onMapWarningScroll
        };
    },
});
